import { BlueprintLocation } from '../types/protocols';

/** A rectangle in client (viewport) pixels, e.g. from getBoundingClientRect(). */
export interface ClientRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** The marker coordinate reference: the drawing node's raster size (plan §3.3). */
export interface ReferenceSize {
    width: number;
    height: number;
}

/**
 * Client point → BlueprintLocation in the drawing's pixel space.
 *
 * `box` is the drawn box on screen (already including zoom and pan) and
 * `reference` is the drawing node's `width`/`height`. Positions are written as
 * fractions of the box times the reference size, so the result is independent
 * of what is actually drawn in the box (raster `<img>` or vector canvas) and of
 * the raster element's `naturalWidth`.
 *
 * `clamp` snaps points outside the box to its edge (drag); without it, a point
 * outside the box yields null (click).
 */
export function locationFromClientPoint(
    clientX: number,
    clientY: number,
    box: ClientRect,
    reference: ReferenceSize,
    key: string,
    clamp: boolean
): BlueprintLocation | null {
    if (!(reference.width > 0) || !(reference.height > 0)) return null;
    if (!(box.width > 0) || !(box.height > 0)) return null;
    let fx = (clientX - box.left) / box.width;
    let fy = (clientY - box.top) / box.height;
    if (clamp) {
        fx = Math.min(Math.max(fx, 0), 1);
        fy = Math.min(Math.max(fy, 0), 1);
    } else if (fx < 0 || fx > 1 || fy < 0 || fy > 1) {
        return null;
    }
    return {
        key,
        x: Math.round(fx * reference.width),
        y: Math.round(fy * reference.height),
        width: reference.width,
        height: reference.height,
    };
}

/**
 * Where a stored location lands in the unzoomed drawn box (CSS pixels), given
 * the box width. Same fraction convention as every renderer: x / location.width.
 */
export function pointInBox(
    location: Pick<BlueprintLocation, 'x' | 'y' | 'width' | 'height'>,
    boxWidth: number
): { x: number; y: number } | null {
    if (!(location.width > 0) || !(location.height > 0) || !(boxWidth > 0)) return null;
    const scale = boxWidth / location.width;
    return { x: location.x * scale, y: location.y * scale };
}
